import { accountStorageKey } from '@/stores/auth/accountStorage';
import { ReactNode, useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'antd';
import { TbArrowRight } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { toast } from '@/shared/components/toasts/PlatformToast';

const RESOURCE_DRAFTS_STORAGE_KEY = 'countercyclical:workspace-resource-drafts';

export type ResourceDraftType = 'dashboard' | 'folder' | 'investment' | 'list' | 'note' | 'pipeline' | 'task' | 'team';

export interface ResourceDraft<TValues = unknown> {
    id: string;
    resourceType: ResourceDraftType;
    resourceLabel: string;
    title: string;
    values: TValues;
    createdAt: string;
    updatedAt: string;
}

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

export const getResourceDrafts = (): ResourceDraft<unknown>[] => {
    if (!canUseStorage()) {
        return [];
    }

    try {
        return JSON.parse(
            window.localStorage.getItem(accountStorageKey(RESOURCE_DRAFTS_STORAGE_KEY)) || '[]',
        ) as ResourceDraft<unknown>[];
    } catch {
        return [];
    }
};

const setResourceDrafts = (drafts: ResourceDraft<unknown>[]) => {
    if (!canUseStorage()) {
        return;
    }

    window.localStorage.setItem(accountStorageKey(RESOURCE_DRAFTS_STORAGE_KEY), JSON.stringify(drafts));
    window.dispatchEvent(new Event('resource-drafts-change'));
};

export const saveResourceDraft = <TValues,>({
    id,
    resourceType,
    resourceLabel,
    title,
    values,
}: Pick<ResourceDraft<TValues>, 'resourceType' | 'resourceLabel' | 'title' | 'values'> & { id?: string }) => {
    const now = new Date().toISOString();
    const drafts = getResourceDrafts();
    const draft: ResourceDraft<TValues> = {
        id: id || `${resourceType}-${Date.now()}`,
        resourceType,
        resourceLabel,
        title: title?.trim() || `Untitled ${resourceLabel}`,
        values,
        createdAt: drafts.find((d) => d.id === id)?.createdAt || now,
        updatedAt: now,
    };

    setResourceDrafts([draft, ...drafts.filter((d) => d.id !== draft.id)]);

    return draft;
};

export const removeResourceDraft = (id?: string) => {
    if (!id) {
        return;
    }

    setResourceDrafts(getResourceDrafts().filter((draft) => draft.id !== id));
};

export const removeResourceDrafts = (ids: string[]) => {
    if (!ids.length) {
        return;
    }

    const draftIds = new Set(ids);

    setResourceDrafts(getResourceDrafts().filter((draft) => !draftIds.has(draft.id)));
};

export const useResourceDraftList = () => {
    const [drafts, setDrafts] = useState<ResourceDraft[]>(() => getResourceDrafts());

    useEffect(() => {
        if (!canUseStorage()) {
            return;
        }

        const updateDrafts = () => setDrafts(getResourceDrafts());

        window.addEventListener('storage', updateDrafts);
        window.addEventListener('resource-drafts-change', updateDrafts);

        return () => {
            window.removeEventListener('storage', updateDrafts);
            window.removeEventListener('resource-drafts-change', updateDrafts);
        };
    }, []);

    return drafts;
};

interface UseDirtyResourceDraftOptions<TValues> {
    close: () => void;
    draftId?: string;
    isDirty: boolean;
    reset?: () => void;
    resourceLabel: string;
    resourceType: ResourceDraftType;
    title: string;
    values: TValues;
}

export const useDirtyResourceDraft = <TValues,>({
    close,
    draftId,
    isDirty,
    reset,
    resourceLabel,
    resourceType,
    title,
    values,
}: UseDirtyResourceDraftOptions<TValues>): {
    dirtyDraftModal: ReactNode;
    markSubmitted: () => void;
    requestClose: () => void;
} => {
    const [confirmOpen, setConfirmOpen] = useState(false);

    const discard = () => {
        removeResourceDraft(draftId);
        reset?.();
        setConfirmOpen(false);
        close();
    };

    const saveDraft = () => {
        saveResourceDraft({
            id: draftId,
            resourceType,
            resourceLabel,
            title,
            values,
        });

        reset?.();
        setConfirmOpen(false);
        close();

        toast('Draft Saved', {
            description: title?.trim() || `Untitled ${resourceLabel}`,
        });
    };

    return {
        requestClose: () => {
            if (isDirty) {
                setConfirmOpen(true);
                return;
            }

            close();
        },
        markSubmitted: () => {
            removeResourceDraft(draftId);
        },
        dirtyDraftModal: (
            <Modal
                getContainer={() => document.body}
                mask={true}
                open={confirmOpen}
                title='Save as Draft?'
                onCancel={() => setConfirmOpen(false)}
                zIndex={1100}
                destroyOnHidden
                closable
                footer={
                    <Row wrap={false} align='middle' gutter={[12, 0]}>
                        <Col flex='none'>
                            <Button onClick={discard}>Discard</Button>
                        </Col>
                        <Col flex='auto'></Col>
                        <Col flex='none'>
                            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        </Col>
                        <Col flex='none'>
                            <Button type='primary' onClick={saveDraft}>
                                Save
                                <TbArrowRight />
                            </Button>
                        </Col>
                    </Row>
                }
            >
                Want to save this as a draft before closing? You can always access it later in{' '}
                <Link to={'/drafts'}>Drafts</Link>
            </Modal>
        ),
    };
};

interface UseDirtyFormDraftOptions {
    close: () => void;
    discard: () => void;
    isDirty: boolean;
    save: () => Promise<void>;
}

export const useDirtyFormDraft = ({ close, discard, isDirty, save }: UseDirtyFormDraftOptions) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const discardAndClose = () => {
        discard();
        setConfirmOpen(false);
        close();
    };

    const saveAndClose = async () => {
        setSaving(true);

        try {
            await save();
            setConfirmOpen(false);
            close();
        } catch {
            return;
        } finally {
            setSaving(false);
        }
    };

    return {
        requestClose: () => {
            if (isDirty) {
                setConfirmOpen(true);
                return;
            }

            discardAndClose();
        },
        dirtyDraftModal: (
            <Modal
                getContainer={() => document.body}
                mask
                open={confirmOpen}
                title='Unsaved Changes'
                onCancel={() => setConfirmOpen(false)}
                zIndex={1100}
                destroyOnHidden
                closable
                footer={
                    <Row wrap={false} align='middle' gutter={[12, 0]}>
                        <Col flex='none'>
                            <Button onClick={discardAndClose}>Discard</Button>
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <Button onClick={() => setConfirmOpen(false)}>Keep Editing</Button>
                        </Col>
                        <Col flex='none'>
                            <Button type='primary' loading={saving} onClick={() => void saveAndClose()}>
                                Save Changes
                                <TbArrowRight />
                            </Button>
                        </Col>
                    </Row>
                }
            >
                This report component has unsaved changes. Save them before closing or discard the draft.
            </Modal>
        ),
    };
};
